import { useEffect, useState } from "react";
import { PeopleList } from "./PeopleSettings";
import { SettingsSection } from "../app/SettingsSection";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { Button, Chip, Input, Select } from "../ui";
import { useSession } from "../app/session";
import { useCoreDoc } from "../app/useCoreDoc";
import { useCircle } from "./useCircle";
import { PEOPLE_CHANGED } from "./PeopleSettings";
import { PresenceSettings } from "./PresenceSettings";
import { setSetting, useSettings } from "../app/settings";

interface PublicProfile {
  id: string;
  name: string;
  kind: string;
  isAdmin: boolean;
}

/**
 * Settings → Circle (spec §9.29): shared spaces and their members, invites with a QR. Profiles are under
 * Settings → People (P3).
 * The sharing rule is shown verbatim. No scoreboard: the fairness chart is a switch, off by default.
 */
export function CircleSettings() {
  const { t } = useTranslation();
  const session = useSession((s) => s.session);
  const load = useSession((s) => s.load);
  const select = useSession((s) => s.selectWorkspace);
  const workspaceId = useSession((s) => s.workspaceId);
  const { handle } = useCoreDoc();
  const circle = useCircle(handle);
  const settings = useSettings(handle);
  const [profiles, setProfiles] = useState<PublicProfile[]>([]);
  const [space, setSpace] = useState("");
  const [invite, setInvite] = useState<{ url: string; qr: string } | null>(null);
  const me = session?.profile;
  const current = session?.workspaces?.find((w) => w.id === workspaceId);

  const refresh = () =>
    fetch("/api/internal/circle/profiles")
      .then((r) => r.json())
      .then((b: { profiles: PublicProfile[] }) => setProfiles(b.profiles));
  useEffect(() => {
    if (session?.mode !== "circle") return;
    void refresh();
    const again = () => void refresh();
    window.addEventListener(PEOPLE_CHANGED, again);
    return () => window.removeEventListener(PEOPLE_CHANGED, again);
  }, [session?.mode]);

  if (session?.mode !== "circle" || !me || me.kind !== "person") return null;
  const json = (method: string, path: string, body: unknown) =>
    fetch(path, {
      method,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

  return (
    <SettingsSection
      id="settings-circle"
      intro={t("settings.intro.circle")}
      summary={t("settings.summary.people")}
      title={t("people.title")}
      testId="settings-circle"
    >
      <p className="text-[13px] text-neutral-700 dark:text-neutral-300" data-testid="sharing-rule">
        {t("circle.rule")}
      </p>

      <div>
        <h3 className="mb-1.5 text-[12.5px] text-neutral-500">{t("circle.sharedSpaces")}</h3>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!space.trim()) return;
            const res = await json("POST", "/api/internal/circle/workspaces", { name: space.trim() });
            if (res.ok) {
              const b = (await res.json()) as { workspace: { id: string } };
              await load();
              select(b.workspace.id);
              setSpace("");
            }
          }}
        >
          <Input
            className="w-48"
            value={space}
            onChange={(e) => setSpace(e.target.value)}
            placeholder={t("circle.newSpace")}
            aria-label={t("circle.newSpace")}
            data-testid="space-name"
          />
          <Button type="submit" variant="secondary" size="sm" data-testid="space-create">
            {t("common.add")}
          </Button>
        </form>
      </div>

      {current?.shared && current.role === "owner" && (
        <div data-testid="space-members">
          <h3 className="mb-1.5 text-[12.5px] text-neutral-500">
            {t("circle.membersOf", { name: current.name })}
          </h3>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {circle.members.map((m) => (
              <Chip
                key={m.id}
                tone={m.color}
                removeLabel={t("common.remove")}
                onRemove={
                  m.id === me.id
                    ? undefined
                    : () =>
                        void json("PATCH", "/api/internal/circle/workspaces", {
                          id: current.id,
                          remove: m.id,
                        })
                }
              >
                {m.name} · {t(`circle.role.${m.role}`)}
              </Chip>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              aria-label={t("circle.addMember")}
              value=""
              data-testid="member-add"
              onChange={(e) =>
                e.target.value &&
                void json("PATCH", "/api/internal/circle/workspaces", { id: current.id, add: e.target.value })
              }
            >
              <option value="">{t("circle.addMember")}</option>
              {profiles
                .filter((p) => !circle.members.some((m) => m.id === p.id))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </Select>
            <Button
              variant="secondary"
              size="sm"
              data-testid="invite-create"
              onClick={async () => {
                const res = await json("POST", "/api/internal/circle/invites", {
                  kind: "person",
                  workspaces: [current.id],
                });
                if (!res.ok) return;
                const { url } = (await res.json()) as { url: string };
                setInvite({ url, qr: await QRCode.toDataURL(url, { margin: 1, width: 180 }) });
              }}
            >
              {t("circle.invite")}
            </Button>
          </div>
          {invite && (
            <div className="mt-3 flex items-center gap-3" data-testid="invite">
              <img
                src={invite.qr}
                alt={t("circle.inviteQr")}
                width={120}
                height={120}
                className="rounded-md border border-neutral-200 dark:border-neutral-800"
              />
              <div className="min-w-0">
                <p className="break-all font-mono text-[12px]" data-testid="invite-url">
                  {invite.url}
                </p>
                <p className="mt-1 text-[12px] text-neutral-500">{t("circle.inviteHint")}</p>
              </div>
            </div>
          )}
          <label className="mt-3 flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={Boolean(
                (settings as unknown as { circle?: { fairnessChart?: boolean } }).circle?.fairnessChart,
              )}
              onChange={(e) => handle && setSetting(handle.doc, "circle.fairnessChart", e.target.checked)}
              data-testid="fairness-toggle"
            />
            {t("circle.fairness")}
            <span className="text-[12px] text-neutral-500">{t("circle.fairnessHint")}</span>
          </label>
        </div>
      )}
      <PeopleList />
    </SettingsSection>
  );
}

/** Presence (Just me, UI cleanup U5): where the person shares being here, in the shared workspace open now. */
export function PresenceSection() {
  const { t } = useTranslation();
  const session = useSession((s) => s.session);
  const workspaceId = useSession((s) => s.workspaceId);
  const current = session?.workspaces?.find((w) => w.id === workspaceId);
  if (session?.mode !== "circle" || !current?.shared || session.profile?.kind !== "person") return null;
  return (
    <SettingsSection
      id="settings-presence"
      intro={t("presence.sectionHint")}
      title={t("presence.title")}
      testId="settings-presence"
    >
      <PresenceSettings workspaceId={current.id} />
    </SettingsSection>
  );
}
